import React, { useState } from 'react';
import {
  Avatar, Button, Card, Col, Descriptions, Empty, List, Modal, Row, Space, Table, Tabs, Tag, Typography, Input, message,
} from 'antd';
import {
  CopyOutlined, DeleteOutlined, EditOutlined, EyeOutlined, FileOutlined, HomeOutlined, PlusOutlined, UserOutlined,
} from '@ant-design/icons';
import { brandSemantic } from '../config/brand';
import { getDisplayNumber } from '../utils/productNumbering';
import { getCertificateStatus, getDaysUntilExpiry } from '../utils/certificateHelpers';
import { formatDate, formatDateTime } from '../utils/formatters';
import AttachmentPreview from '../components/AttachmentPreview';
import SoftTag from '../components/SoftTag';

const { TextArea } = Input;

const { Title, Text } = Typography;

const CERTIFICATE_STATUS_TONES = {
  Expired: { color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder },
  'Expiring Soon': { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
  Valid: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
};

export default function ProductDetailsPage({
  product,
  category,
  suppliers,
  supplierProducts = [],
  documents = [],
  products = [],
  certificates = [],
  productionNotes = [],
  canManage = true,
  onBack,
  onEdit,
  onCopy,
  onAddDocument,
  onEditDocument,
  onDeleteDocument,
  onOpenProduct,
  onVerifyCertificate,
  onAddProductionNote,
  t,
}) {
  const [previewCertificate, setPreviewCertificate] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [newProductionNote, setNewProductionNote] = useState('');

  if (!product) return null;

  const owningSupplier = suppliers.find((item) => item.id === product.supplierId);
  const productProductionNotes = productionNotes
    .filter((note) => note.productId === product.id)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  const productDocuments = documents.filter((doc) => doc.productId === product.id);
  const productImages = productDocuments.filter((doc) => doc.documentType === 'Image');
  const otherDocuments = productDocuments.filter((doc) => doc.documentType !== 'Image');
  const alternativeSupplierProducts = supplierProducts.filter((sp) => sp.productId === product.id);
  const copiedFrom = product.copiedFromProductId
    ? products.find((item) => item.id === product.copiedFromProductId)
    : null;
  const copiedTo = products.filter((item) => item.copiedFromProductId === product.id);

  const itemPrices = alternativeSupplierProducts
    .map((sp) => sp.avgItemPrice)
    .filter((price) => price != null && price > 0);
  const lowestItemPrice = itemPrices.length > 0 ? Math.min(...itemPrices) : null;

  const enrichCertificate = (cert) => ({
    ...cert,
    status: getCertificateStatus(cert.expiryDate),
    daysUntilExpiry: getDaysUntilExpiry(cert.expiryDate),
  });
  const productCertificates = certificates
    .filter((cert) => cert.scope === 'Product' && cert.productId === product.id)
    .map(enrichCertificate);
  const supplierCertificates = certificates
    .filter((cert) => cert.scope === 'Supplier' && cert.supplierId === product.supplierId)
    .map(enrichCertificate);
  const allComplianceCertificates = [...productCertificates, ...supplierCertificates];
  const complianceSummary = {
    valid: allComplianceCertificates.filter((cert) => cert.status === 'Valid').length,
    expiring: allComplianceCertificates.filter((cert) => cert.status === 'Expiring Soon').length,
    expired: allComplianceCertificates.filter((cert) => cert.status === 'Expired').length,
    unverified: allComplianceCertificates.filter((cert) => cert.inherited && !cert.verifiedAt).length,
  };

  const imagesCard = (
    <Card
      size="small"
      title={(
        <Space>
          <FileOutlined />
          <span>{t('Images')}</span>
        </Space>
      )}
      extra={(
        <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => onAddDocument?.()}>
          {t('Add Image')}
        </Button>
      )}
      style={{ height: '100%' }}
    >
      {productImages.length === 0 ? (
        <Text type="secondary">{t('No images yet')}</Text>
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {productImages.map((image) => (
            <div key={image.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
              <Space align="start" size={8}>
                <img
                  src={image.fileUrl}
                  alt={image.name}
                  style={{
                    width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #E5E9F0', cursor: 'pointer',
                  }}
                  onClick={() => setPreviewImage({ name: image.name, url: image.fileUrl })}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{image.name}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>{formatDateTime(image.uploadDate)}</Text>
                </div>
              </Space>
              <Space direction="vertical" size={0}>
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => onEditDocument?.(image)}
                />
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => onDeleteDocument?.(image)}
                />
              </Space>
            </div>
          ))}
        </Space>
      )}
    </Card>
  );

  const overviewTab = (
    <Card style={{ borderRadius: 16 }}>
      <Row gutter={24}>
        <Col xs={24} md={16}>
          <Descriptions column={2} bordered>
            {product.productType === 'Standard' && (
              <Descriptions.Item label={t('Avg. item price')}>
                {lowestItemPrice != null ? `${lowestItemPrice.toLocaleString('da-DK')} DKK` : '—'}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('Category')}>{category?.name || t('Uncategorized')}</Descriptions.Item>
            <Descriptions.Item label={t('Supplier')}>{owningSupplier?.name || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('Supplier Item Number')}>{product.supplierItemNumber || '—'}</Descriptions.Item>
            {product.productType === 'Standard' ? (
              <>
                <Descriptions.Item label={t('Material')}>{product.material || '—'}</Descriptions.Item>
                <Descriptions.Item label={t('Country of Origin')}>{product.countryOfOrigin || '—'}</Descriptions.Item>
              </>
            ) : (
              <>
                <Descriptions.Item label={t('Specifications')} span={2}>{product.specifications || '—'}</Descriptions.Item>
                <Descriptions.Item label={t('Materials')}>{product.materials || '—'}</Descriptions.Item>
              </>
            )}
            {product.packaging && (
              <Descriptions.Item label={t('Packaging')}>{product.packaging.unitPackaging || '—'}</Descriptions.Item>
            )}
            <Descriptions.Item label={t('Specification')} span={2}>{product.description || '—'}</Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} md={8}>
          {imagesCard}
        </Col>
      </Row>
    </Card>
  );

  const documentsTab = (
    <Card style={{ borderRadius: 16 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button type="link" icon={<PlusOutlined />} onClick={() => onAddDocument?.()}>
          {t('Add Document')}
        </Button>
      </Space>
      <Table
        rowKey="id"
        dataSource={otherDocuments}
        pagination={false}
        bordered={false}
        locale={{ emptyText: t('No documents yet') }}
        columns={[
          {
            title: t('Document Name'),
            key: 'name',
            render: (_, record) => (
              <Space direction="vertical" size={0}>
                <strong>{record.name}</strong>
                {record.inherited && <Tag color="gold">{t('Inherited — verification required')}</Tag>}
              </Space>
            ),
          },
          { title: t('Document Type'), dataIndex: 'documentType', key: 'documentType', render: (value) => t(value) },
          {
            title: t('Uploaded File'),
            key: 'file',
            render: (_, record) => (
              <AttachmentPreview files={[{ uid: record.id, name: record.fileName, url: record.fileUrl }]} />
            ),
          },
          {
            title: t('Actions'),
            key: 'actions',
            width: 110,
            render: (_, record) => (
              <Space direction="vertical" size={2}>
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => onEditDocument?.(record)}
                >
                  {t('Edit')}
                </Button>
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => onDeleteDocument?.(record)}
                >
                  {t('Delete')}
                </Button>
              </Space>
            ),
          },
        ]}
      />
    </Card>
  );

  const buildCertificateColumns = () => [
    {
      title: t('Document / Certificate'),
      key: 'document',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <strong>{record.name}</strong>
          {record.inherited && !record.verifiedAt && (
            <Tag color="gold" style={{ marginTop: 4, width: 'fit-content' }}>
              {t('Inherited — verification required')}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('Type / Standard'),
      key: 'typeStandard',
      render: (_, record) => (
        <div>
          <div>{record.documentType || <span style={{ color: '#bfbfbf' }}>—</span>}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.standard}</Text>
        </div>
      ),
    },
    {
      title: t('Valid To'),
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      render: (value) => (value ? formatDate(value) : <span style={{ color: '#bfbfbf' }}>—</span>),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <SoftTag tone={CERTIFICATE_STATUS_TONES[status] || CERTIFICATE_STATUS_TONES.Valid}>{t(status)}</SoftTag>,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => setPreviewCertificate({ url: record.fileUrl, name: record.fileName })}
          >
            {t('Preview')}
          </Button>
          {record.inherited && !record.verifiedAt && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => onVerifyCertificate?.(record.id)}
            >
              {t('Verify Document')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const complianceTab = (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title={t('Product Compliance')}
        extra={(
          <Text type="secondary">
            {t('{{valid}} valid · {{expiring}} expiring · {{unverified}} unverified', complianceSummary)}
          </Text>
        )}
        style={{ borderRadius: 16 }}
      >
        {productCertificates.length === 0 ? (
          <Empty description={t('No certificates yet')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            rowKey="id"
            dataSource={productCertificates}
            columns={buildCertificateColumns()}
            pagination={false}
            bordered={false}
          />
        )}
      </Card>
      <Card title={t('Inherited from Supplier')} style={{ borderRadius: 16 }}>
        {supplierCertificates.length === 0 ? (
          <Empty description={t('No suppliers assigned yet')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            rowKey="id"
            dataSource={supplierCertificates}
            columns={buildCertificateColumns()}
            pagination={false}
            bordered={false}
          />
        )}
      </Card>
    </Space>
  );

  const historyTab = (
    <Card style={{ borderRadius: 16 }}>
      <Descriptions column={1} bordered>
        <Descriptions.Item label={t('Copied From')}>
          {copiedFrom ? (
            <Button type="link" style={{ padding: 0 }} onClick={() => onOpenProduct?.(copiedFrom)}>
              {copiedFrom.name} ({getDisplayNumber(copiedFrom)})
            </Button>
          ) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('{{count}} products were copied from this one', { count: copiedTo.length })}>
          {copiedTo.length === 0 ? '—' : (
            <Space direction="vertical" size={4}>
              {copiedTo.map((item) => (
                <Button
                  key={item.id}
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => onOpenProduct?.(item)}
                >
                  {item.name} ({getDisplayNumber(item)})
                </Button>
              ))}
            </Space>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('Created By')}>{product.createdBy || '—'}</Descriptions.Item>
        <Descriptions.Item label={t('Created')}>{product.createdAt || '—'}</Descriptions.Item>
      </Descriptions>
    </Card>
  );

  const handleAddProductionNote = () => {
    if (!newProductionNote.trim()) {
      message.warning(t('Please enter a note'));
      return;
    }
    onAddProductionNote?.(product.id, newProductionNote.trim());
    setNewProductionNote('');
  };

  const productionNotesTab = (
    <Card style={{ borderRadius: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <TextArea
          rows={3}
          placeholder={t('Add a new production note...')}
          value={newProductionNote}
          onChange={(event) => setNewProductionNote(event.target.value)}
          style={{ marginBottom: 8 }}
        />
        <Button type="primary" onClick={handleAddProductionNote}>
          {t('Add Note')}
        </Button>
      </div>
      {productProductionNotes.length === 0 ? (
        <Empty description={t('No production notes yet')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          dataSource={productProductionNotes}
          renderItem={(note) => (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={(
                  <Space>
                    <strong>{note.author}</strong>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDateTime(note.timestamp)}
                    </Text>
                  </Space>
                )}
                description={note.note}
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Button icon={<HomeOutlined />} onClick={onBack}>
          {t('Back to Products')}
        </Button>
        {canManage && (
          <Space>
            <Button icon={<CopyOutlined />} onClick={() => onCopy?.(product)}>
              {t('Copy this Product')}
            </Button>
            <Button icon={<EditOutlined />} onClick={() => onEdit(product)}>
              {t('Edit Product')}
            </Button>
          </Space>
        )}
      </div>

      <Card style={{ borderRadius: 16 }}>
        <Space wrap size={12} align="center">
          <Title level={1} style={{ margin: 0, fontSize: 34, lineHeight: 1.1 }}>{product.name}</Title>
          <Tag color={product.productType === 'Custom' ? 'purple' : 'blue'}>
            {t(product.productType === 'Custom' ? 'Custom Product' : 'Standard Product')}
          </Tag>
        </Space>
      </Card>

      <Tabs
        items={[
          { key: 'overview', label: t('Overview'), children: overviewTab },
          { key: 'documents', label: t('Documents'), children: documentsTab },
          { key: 'compliance', label: t('Compliance'), children: complianceTab },
          { key: 'productionNotes', label: t('Production Notes'), children: productionNotesTab },
          { key: 'history', label: t('History'), children: historyTab },
        ]}
      />

      <Modal
        title={previewCertificate?.name}
        open={Boolean(previewCertificate)}
        onCancel={() => setPreviewCertificate(null)}
        footer={null}
        width="80%"
        styles={{ body: { padding: 0, height: '80vh' } }}
        destroyOnClose
      >
        {previewCertificate && (
          <iframe
            src={previewCertificate.url}
            title={previewCertificate.name}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        )}
      </Modal>

      <Modal
        title={previewImage?.name}
        open={Boolean(previewImage)}
        onCancel={() => setPreviewImage(null)}
        footer={null}
        width="80%"
        styles={{ body: { padding: 0, height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
        destroyOnClose
      >
        {previewImage && (
          <img
            src={previewImage.url}
            alt={previewImage.name}
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          />
        )}
      </Modal>
    </div>
  );
}
